import { useCheckingProfile, ValidationItem } from "@/api/master-profile";
import { NNEvidenceData, NNEvidenceInvalid } from "@/api/nn-evidence";
import { useMemo } from "react";

export type OverallStatus =
  | "Unvalidated"
  | "Validated"
  | "Partially Validated"
  | "Validation Bypassed"
  | "Optional"
  | "Loading";

export interface ValidationResult extends ValidationItem {
  isValid: boolean | null;
}

export function calculateNNValidationStatus(
  processedValidations: Array<
    Pick<ValidationResult, "code" | "isValidateNow" | "isValid">
  >,
): OverallStatus {
  const validateNowItems = processedValidations.filter(
    (v) => v.isValidateNow && v.code !== "B00",
  );
  const validateLaterItems = processedValidations.filter(
    (v) => !v.isValidateNow && v.code !== "B00",
  );

  const allNowItemsValid = validateNowItems.every((item) => item.isValid);
  const allLaterItemsValid = validateLaterItems.every((item) => item.isValid);

  // Any invalid required rule means the submission is immediately unvalidated.
  if (!allNowItemsValid) {
    return "Unvalidated";
  }

  // Required rules are valid, but at least one later rule is still invalid.
  if (allNowItemsValid && !allLaterItemsValid) {
    return "Partially Validated";
  }

  // Required and later rules are all valid.
  if (allNowItemsValid && allLaterItemsValid) {
    return "Validated";
  }

  // Safety fallback for unexpected states.
  return "Unvalidated";
}

const VALID_VALIDATION_STRINGS = {
  DUPLICATED: "Duplicated",
  DUPLICATED_HOUSE_NUMBER_NN: "Duplicated House Number NN",
  HOUSE_NUMBER_ABD: "House Number ABD",
  HOUSE_NUMBER_NN: "House Number NN",
};

export default function useNNEvidenceValidations({
  checkingProfileId,
  nnEvidenceInvalids,
  nnEvidence,
}: {
  checkingProfileId: string | undefined;
  nnEvidenceInvalids: NNEvidenceInvalid[];
  nnEvidence?: NNEvidenceData;
}) {
  const { data: checkingProfileData } = useCheckingProfile(
    { id: checkingProfileId ?? "" },
    { enabled: !!checkingProfileId },
  );

  const validationSummary = useMemo(() => {
    if (!checkingProfileData?.validations?.["Evidence NN"]) {
      return {
        requiredList: [],
        unrequiredList: [],
        overallStatus: "Unvalidated" as const,
      };
    }

    const allValidations = checkingProfileData?.validations?.["Evidence NN"];
    const sourceData: string[] = [
      ...new Set(
        nnEvidenceInvalids?.map((item) => {
          // Extract the text inside brackets when the API provides a tagged error.
          const match = /\[(.*?)\]/.exec(item.result);
          // Fall back to the original message when no bracketed tag is found.
          return match ? match[1] : item?.result;
        }),
      ),
    ];

    const processedValidations: ValidationResult[] = allValidations.map(
      (item: ValidationItem) => {
        const validator = validationStrategies[item.code] ?? defaultValidator;
        const isValid = validator(sourceData, nnEvidence);

        return {
          ...item,
          isValid,
        };
      },
    );

    let overallStatus: OverallStatus = "Loading";
    const isRequiredRule = allValidations.find(
      (v: ValidationItem) => v.code === "B00",
    );
    const otherRules = allValidations.filter(
      (v: ValidationItem) => v.code !== "B00",
    );

    const requiredList = processedValidations.filter(
      (item: { isValidateNow: boolean | null; code: string }) =>
        item?.isValidateNow &&
        item?.isValidateNow !== null &&
        item?.code !== "B00",
    );
    const unrequiredList = processedValidations.filter(
      (item: { isValidateNow: boolean | null; code: string }) =>
        !item?.isValidateNow &&
        item?.isValidateNow !== null &&
        item?.code !== "B00",
    );

    // When the status is not Optional or Bypassed, derive it from the validation results.
    if (overallStatus === "Loading") {
      overallStatus = calculateNNValidationStatus(processedValidations);
    }

    // If the evidence itself is invalid, the overall result must be Unvalidated.
    if (!nnEvidence) {
      overallStatus = "Unvalidated";
    }

    // Optional means every rule, including the required rule, is explicitly unset.
    const isOptionalStatus =
      isRequiredRule?.isValidateNow === null &&
      otherRules.every((r: ValidationItem) => r.isValidateNow === null);

    if (isOptionalStatus) {
      return {
        requiredList,
        unrequiredList,
        overallStatus: "Optional",
      };
    }

    // Validation bypassed means the required rule is active and all other rules are not scheduled.
    if (isRequiredRule) {
      if (
        typeof isRequiredRule.isValidateNow === "boolean" &&
        otherRules.every((r: ValidationItem) => r.isValidateNow === null)
      ) {
        overallStatus = "Validation Bypassed";
      }
    }

    // A valid evidence without a document path is treated as Validated.
    if (
      nnEvidence?.valid === true &&
      nnEvidence?.documentNnEvidencePath === null
    ) {
      overallStatus = "Validated";
    }

    return {
      requiredList,
      unrequiredList,
      overallStatus,
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nnEvidenceInvalids, checkingProfileData]);

  return { ...validationSummary };
}

const validationStrategies: Record<
  string,
  (dataSource: string[], nnEvidence?: NNEvidenceData) => boolean
> = {
  B01: (dataSource, nnEvidence) => {
    // B01 passes only when there is no duplicated evidence tag in the source data.
    if (!nnEvidence) {
      return false;
    }

    const isValid = !dataSource.includes(VALID_VALIDATION_STRINGS.DUPLICATED);

    // If the list contains unrelated errors, the rule still fails.
    if (dataSource.length && isValid) {
      return false;
    }

    return isValid;
  },
  B02: (dataSource, nnEvidence) => {
    // B02 uses the same duplicated-tag rule as B01.
    if (!nnEvidence) {
      // Evidence is required before this rule can be evaluated.
      return false;
    }

    const isValid = !dataSource.includes(VALID_VALIDATION_STRINGS.DUPLICATED);

    // Non-empty source data with unrelated tags still fails this rule.
    if (dataSource.length && isValid) {
      return false;
    }

    return isValid;
  },
  B03: (dataSource, nnEvidence) => {
    // B03 fails when the evidence contains the duplicated house number NN tag.
    if (!nnEvidence) {
      // Evidence is required before this rule can be evaluated.
      return false;
    }

    const isValid = !dataSource.includes(
      VALID_VALIDATION_STRINGS.DUPLICATED_HOUSE_NUMBER_NN,
    );

    // Non-empty source data with unrelated tags still fails this rule.
    if (dataSource.length && isValid) {
      return false;
    }

    return isValid;
  },
  B04: (dataSource, nnEvidence) => {
    // B04 fails when either ABD or NN house number tags are present.
    if (!nnEvidence) {
      // Evidence is required before this rule can be evaluated.
      return false;
    }

    const isValid = !(
      dataSource.includes(VALID_VALIDATION_STRINGS.HOUSE_NUMBER_ABD) ||
      dataSource.includes(VALID_VALIDATION_STRINGS.HOUSE_NUMBER_NN)
    );

    // Non-empty source data with unrelated tags still fails this rule.
    if (dataSource.length && isValid) {
      return false;
    }

    return isValid;
  },
};

const defaultValidator: (
  dataSource: string[],
  nnEvidence?: NNEvidenceData,
) => void = (dataSource, nnEvidence) => {
  // Unknown rules are treated as invalid when evidence is missing or any error exists.
  if (!nnEvidence) {
    // Cannot validate unknown rule without evidence context.
    return false;
  }

  // Any extracted error tag marks unknown rules as failed.
  if (dataSource?.length) {
    return false;
  }

  return true;
};
